import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ToastContainer from '../../components/common/ToastContainer';
import { 
    MagnifyingGlassIcon, 
    UserIcon, 
    ChevronDownIcon, 
    CheckIcon
} from '@heroicons/react/24/outline';
import Modal from '../../components/common/Modal';
import FilterDropdown from '../../components/common/FilterDropdown';
import SearchableDropdown from '../../components/common/SearchableDropdown';
import PremiumTooltip from '../../components/common/PremiumTooltip';

const CourseList = () => {
    const { get, post, del, patch } = useApi();
    const { showToast } = useAppState();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // State for filtering and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [filterGrade, setFilterGrade] = useState('all');
    const [filterPathway, setFilterPathway] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    // State for Modals and Editing
    const [editingArea, setEditingArea] = useState(null);
    const [deletingArea, setDeletingArea] = useState(null);
    const [saving, setSaving] = useState(false);
    const [splitting, setSplitting] = useState(false);
    const [, setLoadingStreams] = useState(false);
    const [availableStreams, setAvailableStreams] = useState([]);
    const [streamAssignments, setStreamAssignments] = useState([]);
    const [error, setError] = useState(null);
    const [activeStreamIndex, setActiveStreamIndex] = useState(null);
    const [teacherSearch, setTeacherSearch] = useState('');
    const [pathwayChoices, setPathwayChoices] = useState([]);
    const [showUnifyConfirm, setShowUnifyConfirm] = useState(false);

    // 1. Learning Areas Query
    const { data: courses = [], isLoading: loading } = useQuery({
        queryKey: ['learning-areas'],
        queryFn: () => get('/api/cbc/learning-areas/'),
        staleTime: 2 * 60 * 1000,
    });

    // 2. Teachers Query
    const { data: teachers = [] } = useQuery({
        queryKey: ['admin-teachers'],
        queryFn: () => get('/api/admin/teachers/'),
        staleTime: 5 * 60 * 1000,
    });

    // 3. Grade Levels Query
    const { data: gradeLevels = [] } = useQuery({
        queryKey: ['grade-levels'],
        queryFn: () => get('/api/cbc/grade-levels/'),
        staleTime: 30 * 60 * 1000,
    });

    // Fetch pathway choices
    useEffect(() => {
        const fetchPathways = async () => {
            try {
                const data = await get('/api/cbc/learning-areas/pathway_choices/');
                setPathwayChoices(data);
            } catch (err) {
                console.error('Error fetching pathways:', err);
            }
        };
        fetchPathways();
    }, [get]);

    // Fetch streams when the editing area's grade level changes
    useEffect(() => {
        const fetchStreamsForGrade = async () => {
            if (editingArea?.grade_level) {
                setLoadingStreams(true);
                try {
                    const gradeId = editingArea.grade_level?.id || editingArea.grade_level;
                    const data = await get(`/api/cbc/streams/?grade_level=${gradeId}`);
                    setAvailableStreams(data);
                } catch (error) {
                    console.error('Error fetching streams:', error);
                    setAvailableStreams([]);
                } finally {
                    setLoadingStreams(false);
                }
            } else {
                setAvailableStreams([]);
            }
        };
        fetchStreamsForGrade();
    }, [editingArea?.grade_level, get]);

    // When the fetched streams list is ready, merge with existing assignments
    // so ALL streams for the grade are always shown in the Faculty Map
    useEffect(() => {
        if (availableStreams.length === 0) return;
        const existingMap = {};
        (editingArea?.stream_assignments || []).forEach(a => {
            existingMap[a.stream_id] = a;
        });
        const merged = availableStreams.map(s => ({
            stream_id: s.id,
            stream_name: s.name,
            teacher_id: existingMap[s.id]?.teacher_id || null,
            teacher_name: existingMap[s.id]?.teacher_name || 'Unassigned',
            is_split: !!existingMap[s.id],
        }));
        setStreamAssignments(merged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableStreams]);

    const handleOpenEdit = async (course) => {
        setEditingArea(course);
        setError(null);
        if (course.id) {
            try {
                const detailedArea = await get(`/api/cbc/learning-areas/${course.id}/?exclude_content=true`);
                setEditingArea(detailedArea);
                // Build initial streamAssignments from already-split data,
                // merging with all available streams so every stream is shown
                const existingMap = {};
                (detailedArea.stream_assignments || []).forEach(a => {
                    existingMap[a.stream_id] = a;
                });
                // Will be completed once availableStreams loads (see effect below)
                setStreamAssignments(detailedArea.stream_assignments || []);
            } catch (err) {
                console.error('Error fetching detailed area:', err);
            }
        } else {
            setStreamAssignments([]);
        }
    };

    const handleUpdateArea = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            const isNew = !editingArea.id;
            const teacherVal = editingArea.teacher?.id || editingArea.teacher;

            // RULE: If any stream-level assignments are being made, the master area
            // must always be 'Global Unassigned'. Never set a master teacher at
            // the same time as stream teacher assignments.
            const hasStreamAssignments = streamAssignments.some(a => a.teacher_id !== null);
            const masterTeacherId = hasStreamAssignments
                ? null
                : (teacherVal && teacherVal !== '' ? parseInt(teacherVal) : null);

            let generatedCode = editingArea.code;
            if (isNew && editingArea.name && editingArea.grade_level) {
                const gradeObj = gradeLevels.find(g => g.id === parseInt(editingArea.grade_level));
                const gradeName = gradeObj ? gradeObj.name : '';
                const gradePart = gradeName.replace(/[^0-9]/g, '');
                const initials = editingArea.name.split(' ').map(w => w[0]).join('').toUpperCase();
                generatedCode = `${initials}-G${gradePart}`;
            }

            const payload = {
                name: editingArea.name,
                code: generatedCode,
                pathway: editingArea.pathway,
                is_active: editingArea.is_active !== undefined ? editingArea.is_active : true,
                teacher: masterTeacherId,
            };

            if (isNew) {
                payload.grade_level = parseInt(editingArea.grade_level);
            }

            const savedArea = await (isNew ? post : patch)(
                isNew ? '/api/cbc/learning-areas/' : `/api/cbc/learning-areas/${editingArea.id}/`,
                payload
            );

            const targetId = isNew ? (savedArea?.id || savedArea?.data?.id) : editingArea.id;

            // Sync stream teacher assignments when any stream has a teacher set
            if (targetId && hasStreamAssignments) {
                await post(`/api/cbc/learning-areas/${targetId}/batch-assign-teachers/`, {
                    assignments: streamAssignments.map(a => ({
                        stream_id: a.stream_id,
                        teacher_id: a.teacher_id
                    }))
                });
            }

            showToast(`${editingArea.name} ${isNew ? 'created' : 'updated'} successfully!`, 'success');

            // Invalidate cache
            queryClient.invalidateQueries(['learning-areas']);

            // Close modal
            setEditingArea(null);
            setStreamAssignments([]);
        } catch (error) {
            console.error('Error updating learning area:', error);
            const errorMessage = error.response?.data?.teacher?.[0]
                || error.response?.data?.detail
                || error.message
                || 'Failed to update learning area';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setSaving(false);
        }
    };



    const handleUnifyStreams = async () => {
        if (!editingArea?.id) return;
        
        setShowUnifyConfirm(false);
        setSplitting(true);
        try {
            const response = await post(`/api/cbc/learning-areas/${editingArea.id}/unify_streams/`);
            showToast(response.message, 'success');
            queryClient.invalidateQueries(['learning-areas']);

            // Refresh detailed instance to show Master Mode
            const updatedArea = await get(`/api/cbc/learning-areas/${editingArea.id}/?exclude_content=true`);
            setEditingArea(updatedArea);
            setStreamAssignments(updatedArea.stream_assignments || []);
        } catch (error) {
            console.error('Error unifying streams:', error);
            showToast(error.response?.data?.error || 'Failed to unify streams', 'error');
        } finally {
            setSplitting(false);
        }
    };

    const handleDelete = async () => {
        if (!deletingArea) return;
        try {
            await del(`/api/cbc/learning-areas/${deletingArea.id}/`);
            queryClient.invalidateQueries(['learning-areas']);
            setDeletingArea(null);
            showToast('Learning Area deleted successfully', 'success');
        } catch (error) {
            console.error('Error deleting learning area:', error);
            showToast('Failed to delete learning area', 'error');
        }
    };

    const filteredCourses = useMemo(() => {
        const codeMap = new Set();
        
        // Sort courses so we process the master area (most content) first
        const sortedCourses = [...courses].sort((a, b) => {
            const aScore = (a.stream_assignments?.length || 0) * 10 + (a.strands_count || 0);
            const bScore = (b.stream_assignments?.length || 0) * 10 + (b.strands_count || 0);
            return bScore - aScore;
        });

        return sortedCourses.filter(course => {
            // Hide stream-specific instances (areas that have streams assigned)
            // These are managed via the master area's Faculty Map
            if (course.streams && course.streams.length > 0) return false;

            // Deduplicate orphans by registry code - keeps only the true master area
            if (codeMap.has(course.code)) return false;
            codeMap.add(course.code);

            const matchesSearch =
                course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                course.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                course.teacher_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                course.stream_assignments?.some(a =>
                    a.teacher_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    a.stream_name?.toLowerCase().includes(searchTerm.toLowerCase())
                );

            const matchesGrade = filterGrade === 'all' || course.grade_level === parseInt(filterGrade);

            const matchesPathway = filterPathway === 'all' || course.pathway === filterPathway;

            return matchesSearch && matchesGrade && matchesPathway;
        });
    }, [courses, searchTerm, filterGrade, filterPathway]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterGrade, filterPathway]);

    const paginatedCourses = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredCourses.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredCourses, currentPage]);

    const totalPages = Math.ceil(filteredCourses.length / ITEMS_PER_PAGE);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-40">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-10 h-10 border-4 border-[#18216D] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading Library...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <ToastContainer />
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-black text-[#18216D]">Learning Areas</h1>
                    <p className="text-slate-400 font-bold text-sm mt-1">Manage all subjects and teacher assignments in the system</p>
                </div>
                <button
                    onClick={() => setEditingArea({ name: '', code: '', is_active: true, teacher: '', grade_level: '' })}
                    className="px-6 py-3 bg-[#18216D] text-white rounded-xl hover:bg-[#0D164F] shadow-lg shadow-indigo-900/10 transition-all font-black text-xs uppercase tracking-widest flex items-center space-x-2"
                >
                    <i className="fas fa-plus"></i>
                    <span>Add Learning Area</span>
                </button>
            </div>

            <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-900/5 border border-slate-100 p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1 relative">
                        <i className="fas fa-search absolute left-5 top-1/2 -translate-y-1/2 text-slate-300"></i>
                        <input
                            type="text"
                            placeholder="Identify by subject name, code, or instructor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-6 py-4 bg-white border-2 border-slate-200 rounded-2xl focus:border-[#FFC425] transition-all text-sm font-medium text-[#18216D] placeholder-slate-300 outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <FilterDropdown
                            value={filterGrade}
                            onChange={setFilterGrade}
                            options={[
                                { name: 'All Grades', value: 'all' },
                                ...gradeLevels.map(grade => ({
                                    name: grade.name,
                                    value: grade.id
                                }))
                            ]}
                        />
                        <FilterDropdown
                            value={filterPathway}
                            onChange={setFilterPathway}
                            options={[
                                { name: 'All Pathways', value: 'all' },
                                { name: 'Core Subjects', value: 'CORE' },
                                { name: 'STEM', value: 'STEM' },
                                { name: 'Social Sciences', value: 'SOCIAL_SCIENCES' },
                                { name: 'Arts & Sports', value: 'ARTS_SPORTS' }
                            ]}
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-900/5 border border-slate-100 overflow-hidden">
                {filteredCourses.length === 0 ? (
                    <div className="text-center py-24 text-slate-300">
                        <div className="text-7xl mb-6 opacity-20">
                            <i className="fas fa-book-open"></i>
                        </div>
                        <p className="text-xl font-black text-[#18216D]">No artifacts found</p>
                        <div className="flex flex-col items-center gap-4 mt-6">
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setFilterPathway('all');
                                    setFilterGrade('all');
                                }}
                                className="px-8 py-3 bg-[#18216D] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-[#0D164F] transition-all"
                            >
                                Reset Search & Filters
                            </button>
                            <Link to="/admin/curriculum" className="text-indigo-600 hover:text-indigo-800 font-black uppercase text-[10px] tracking-widest">
                                Synch Curriculum Registry →
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50/50">
                                <tr>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-600 uppercase tracking-[0.2em]">Learning Area</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-600 uppercase tracking-[0.2em]">Grade</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-600 uppercase tracking-[0.2em]">Teachers</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-600 uppercase tracking-[0.2em] text-center">Strands</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-600 uppercase tracking-[0.2em] text-center">Students</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-600 uppercase tracking-[0.2em] text-center">State</th>
                                    <th className="px-8 py-6 text-[10px] font-black text-slate-600 uppercase tracking-[0.2em] text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {paginatedCourses.map((course) => (
                                    <tr key={course.id} className="hover:bg-indigo-50/30 transition-colors group">
                                        <td className="px-8 py-6">
                                            <div>
                                                <div className="font-black text-[#18216D] group-hover:text-[#18216D] transition-colors text-sm uppercase tracking-tight">{course.name}</div>
                                                <div className="text-xs font-mono font-bold text-slate-400 mt-0.5">{course.code}</div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 whitespace-nowrap">
                                            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                                                {course.grade_level_name}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex flex-wrap gap-1.5 items-center max-w-[280px]">
                                                {course.stream_assignments && course.stream_assignments.length > 0 ? (
                                                    <>
                                                        {(course.stream_assignments.length > 3
                                                            ? course.stream_assignments.slice(0, 3)
                                                            : course.stream_assignments
                                                        ).map((assignment) => (
                                                            <span
                                                                key={assignment.stream_id}
                                                                className="px-2.5 py-1 bg-white border border-indigo-100 text-[#18216D] rounded-lg text-[10px] font-black uppercase tracking-tight shadow-sm whitespace-nowrap"
                                                                title={assignment.teacher_name}
                                                            >
                                                                {assignment.stream_name}: {assignment.teacher_name || 'VACANT'}
                                                            </span>
                                                        ))}
                                                        {course.stream_assignments.length > 3 && (
                                                            <span className="px-2.5 py-1 bg-slate-100 text-slate-400 rounded-lg text-[10px] font-black uppercase tracking-tight border border-slate-200">
                                                                + {course.stream_assignments.length - 3}
                                                            </span>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="text-xs font-bold text-slate-600 italic">
                                                        {course.teacher_name || 'Global Unassigned'}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <span className="text-sm font-black text-[#18216D] bg-indigo-50 w-8 h-8 rounded-full flex items-center justify-center mx-auto border border-indigo-100">{course.strands_count || 0}</span>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <span className="text-sm font-black text-slate-800">{course.student_count || 0}</span>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <span className={`inline-flex px-3 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-full border shadow-sm ${course.is_active
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                                : 'bg-slate-50 text-slate-400 border-slate-100 opacity-60'
                                                }`}>
                                                {course.is_active ? 'Active' : 'Dormant'}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <PremiumTooltip text="Edit Area">
                                                    <button
                                                        onClick={() => handleOpenEdit(course)}
                                                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-indigo-50 text-[#18216D] hover:bg-indigo-100 transition-all shadow-sm border border-indigo-100"
                                                    >
                                                        <i className="fas fa-pen text-xs"></i>
                                                    </button>
                                                </PremiumTooltip>
                                                <PremiumTooltip text="Delete Area">
                                                    <button
                                                        onClick={() => setDeletingArea(course)}
                                                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-all shadow-sm border border-rose-100"
                                                    >
                                                        <i className="fas fa-trash-can text-xs"></i>
                                                    </button>
                                                </PremiumTooltip>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 py-8">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="h-11 w-11 flex items-center justify-center rounded-2xl bg-white border border-slate-100 text-slate-400 hover:text-[#18216D] disabled:opacity-30 transition-all shadow-sm"
                    >
                        <i className="fas fa-chevron-left text-xs"></i>
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i + 1}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`h-11 min-w-11 px-4 flex items-center justify-center rounded-2xl text-xs font-black transition-all shadow-sm border ${currentPage === i + 1
                                ? 'bg-[#18216D] text-white border-[#18216D] shadow-indigo-900/20'
                                : 'bg-white border-slate-100 text-slate-400 hover:border-[#18216D]/20 hover:text-[#18216D]'
                                }`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="h-11 w-11 flex items-center justify-center rounded-2xl bg-white border border-slate-100 text-slate-400 hover:text-[#18216D] disabled:opacity-30 transition-all shadow-sm"
                    >
                        <i className="fas fa-chevron-right text-xs"></i>
                    </button>
                </div>
            )}

            {/* Governance Modal */}
            {editingArea && (
                <div className="fixed inset-0 bg-[#0F172A]/40 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto custom-scrollbar border border-white/20 animate-in zoom-in-95 duration-500">
                        <div className="bg-white border-b border-slate-50 px-10 py-8 relative flex-shrink-0">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 bg-slate-50 rounded-[1.5rem] flex items-center justify-center border border-slate-100 shadow-inner">
                                    <i className="fas fa-pen-nib text-[#FFC425] text-2xl"></i>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-2xl font-black text-[#18216D] uppercase tracking-tight leading-none">
                                        {editingArea.id ? 'Editing Learning Area' : 'New learning area'}
                                    </h3>
                                    <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mt-2">
                                        {editingArea.name || 'Structural Configuration'}
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => { setEditingArea(null); setError(null); setStreamAssignments([]); }} className="absolute top-8 right-10 w-10 h-10 rounded-full hover:bg-slate-50 text-slate-400 flex items-center justify-center transition-colors">
                                <i className="fas fa-times text-xl"></i>
                            </button>
                        </div>

                        <div>
                            <form onSubmit={handleUpdateArea} className="p-10 space-y-10">
                                {error && (
                                    <div className="bg-rose-50 border border-rose-100 text-rose-600 p-5 rounded-2xl text-[11px] font-black uppercase tracking-wider flex items-center gap-3">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        <span>{error}</span>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-8">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Learning Area Name</label>
                                        <input
                                            type="text"
                                            value={editingArea.name}
                                            onChange={(e) => setEditingArea({ ...editingArea, name: e.target.value })}
                                            className="w-full px-6 py-4 bg-white border-2 border-slate-200 focus:border-indigo-500 rounded-2xl transition-all outline-none text-sm font-black text-[#18216D]"
                                            required
                                            placeholder="e.g. Environmental Science"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Subject Teacher</label>
                                        <SearchableDropdown
                                            options={teachers}
                                            selectedValues={editingArea.teacher?.id ? [editingArea.teacher.id] : (editingArea.teacher ? [editingArea.teacher] : [])}
                                            onChange={(id) => setEditingArea({ ...editingArea, teacher: id })}
                                            placeholder="Global Unassigned"
                                            isMultiple={false}
                                            displayKey="name"
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Career Pathway</label>
                                        <FilterDropdown
                                            value={editingArea.pathway || 'CORE'}
                                            onChange={(value) => setEditingArea({ ...editingArea, pathway: value })}
                                            options={pathwayChoices.map(c => ({ name: c.label, value: c.value }))}
                                            placeholder="Select Pathway"
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Academic Grade</label>
                                        <FilterDropdown
                                            value={editingArea.grade_level?.id || editingArea.grade_level || ''}
                                            onChange={(value) => setEditingArea({ ...editingArea, grade_level: value, stream: '' })}
                                            options={gradeLevels.map(g => ({ name: g.name, value: g.id }))}
                                            placeholder="Select Level"
                                            className="w-full"
                                            disabled={!!editingArea.id}
                                        />
                                    </div>
                                </div>



                                {editingArea.grade_level && (
                                    <div className="space-y-8 animate-in slide-in-from-top-4 duration-500">
                                        <div className="flex items-center justify-between border-b border-slate-100 pb-5">
                                            <div>
                                                <h4 className="text-sm font-black text-slate-900 uppercase tracking-widest">Assign Teachers</h4>
                                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.15em] mt-1 italic">Assign Teachers to each stream, changes save with the Update button</p>
                                            </div>
                                            <div className="flex gap-2">
                                                {editingArea.split_stats?.is_fully_split && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowUnifyConfirm(true)}
                                                        disabled={splitting}
                                                        className="px-5 py-2.5 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-100 transition-all flex items-center gap-2 group"
                                                    >
                                                        <i className="fas fa-down-left-and-up-right-to-center group-hover:rotate-12 transition-transform"></i>
                                                        Unify Streams
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {availableStreams.length > 0 ? (
                                            <div className="grid grid-cols-2 gap-6">
                                                {streamAssignments.map((assignment, index) => (
                                                    <div key={assignment.stream_id} className="p-6 bg-slate-50/30 border border-slate-100 rounded-[2rem] hover:bg-white hover:border-indigo-100 transition-all duration-300 group">
                                                        <div className="flex items-center justify-between mb-4">
                                                            <div className="flex items-center gap-3">
                                                                <span className="w-8 h-8 rounded-xl bg-white shadow-sm flex items-center justify-center text-indigo-400 text-xs font-black">
                                                                    {assignment.stream_name.charAt(0)}
                                                                </span>
                                                                <span className="text-[11px] font-black text-[#18216D] uppercase">{assignment.stream_name} Class</span>
                                                            </div>
                                                            <div className={`w-2 h-2 rounded-full ${assignment.is_split ? 'bg-emerald-500' : 'bg-slate-300 animate-pulse'}`}></div>
                                                        </div>
                                                        {(editingArea.teacher?.id || editingArea.teacher) ? (
                                                            <div className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between text-slate-400">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200">
                                                                        <UserIcon className="w-4 h-4 text-slate-300" />
                                                                    </div>
                                                                    <div className="text-left">
                                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-tight">Assigned Faculty</p>
                                                                        <p className="text-[10px] font-bold">Locked: Master Teacher Active</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="relative">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    if (activeStreamIndex === index) {
                                                                        setActiveStreamIndex(null);
                                                                    } else {
                                                                        setActiveStreamIndex(index);
                                                                        setTeacherSearch('');
                                                                    }
                                                                }}
                                                                className={`w-full px-4 py-3 bg-white border ${activeStreamIndex === index ? 'border-indigo-500 shadow-md ring-2 ring-indigo-50' : 'border-slate-200'} rounded-2xl flex items-center justify-between hover:border-indigo-300 hover:shadow-md transition-all group/btn`}
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center border border-slate-100 group-hover/btn:bg-indigo-50 transition-colors">
                                                                        <UserIcon className="w-4 h-4 text-slate-400 group-hover/btn:text-indigo-500" />
                                                                    </div>
                                                                    <div className="text-left">
                                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-tight">Assigned Faculty</p>
                                                                        <p className="text-[11px] font-bold text-[#18216D]">
                                                                            {assignment.teacher_id ? (teachers.find(t => t.id === assignment.teacher_id)?.name || 'Teacher Found') : 'Unassigned'}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <ChevronDownIcon className={`w-4 h-4 text-slate-300 group-hover/btn:text-indigo-400 transition-transform ${activeStreamIndex === index ? 'rotate-180' : ''}`} />
                                                            </button>

                                                            {activeStreamIndex === index && (
                                                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 z-30 p-4 animate-in slide-in-from-top-2 duration-200">
                                                                    <div className="relative mb-3">
                                                                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-300" />
                                                                        <input
                                                                            type="text"
                                                                            placeholder="SEARCH FACULTY..."
                                                                            className="w-full pl-8 pr-3 py-2 bg-slate-50 border-none rounded-xl text-[9px] font-bold text-slate-700 placeholder:text-slate-300 focus:ring-1 focus:ring-indigo-100 transition-all"
                                                                            value={teacherSearch}
                                                                            onChange={(e) => setTeacherSearch(e.target.value)}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            autoFocus
                                                                        />
                                                                    </div>
                                                                    <div className="max-h-48 overflow-y-auto custom-scrollbar space-y-1">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                const newAssignments = [...streamAssignments];
                                                                                newAssignments[index].teacher_id = null;
                                                                                setStreamAssignments(newAssignments);
                                                                                setActiveStreamIndex(null);
                                                                            }}
                                                                            className={`w-full flex items-center justify-between p-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                                                                                assignment.teacher_id === null ? 'bg-indigo-50 text-indigo-600' : 'hover:bg-slate-50 text-slate-400'
                                                                            }`}
                                                                        >
                                                                            <span>Unassigned</span>
                                                                            {assignment.teacher_id === null && <CheckIcon className="w-3 h-3" />}
                                                                        </button>
                                                                        {teachers
                                                                            .filter(t => t.name.toLowerCase().includes(teacherSearch.toLowerCase()))
                                                                            .map(teacher => (
                                                                                <button
                                                                                    key={teacher.id}
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        const newAssignments = [...streamAssignments];
                                                                                        newAssignments[index].teacher_id = teacher.id;
                                                                                        setStreamAssignments(newAssignments);
                                                                                        setActiveStreamIndex(null);
                                                                                    }}
                                                                                    className={`w-full flex items-center justify-between p-2 rounded-xl transition-all ${
                                                                                        assignment.teacher_id === teacher.id ? 'bg-indigo-50 border border-indigo-100' : 'hover:bg-slate-50 border border-transparent'
                                                                                    }`}
                                                                                >
                                                                                    <div className="text-left">
                                                                                        <p className="text-[10px] font-bold text-[#18216D]">{teacher.name}</p>
                                                                                        <p className="text-[8px] font-bold text-slate-400 uppercase">{teacher.email?.split('@')[0]}</p>
                                                                                    </div>
                                                                                    {assignment.teacher_id === teacher.id && <CheckIcon className="w-3 h-3 text-indigo-500" />}
                                                                                </button>
                                                                            ))
                                                                        }
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="py-12 bg-slate-50 border-2 border-dashed border-slate-100 rounded-[2.5rem] text-center">
                                                <i className="fas fa-layer-group text-4xl text-slate-200 mb-4 block"></i>
                                                <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest leading-relaxed">No Streams Configured</p>
                                                <p className="text-[9px] text-slate-300 font-bold uppercase mt-1">Create streams in Grade Registry to enable per-stream faculty assignment</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="pt-10 flex gap-4 sticky bottom-0 bg-white/95 backdrop-blur-md pb-4 z-20 mt-10">
                                    <button
                                        type="button"
                                        onClick={() => { setEditingArea(null); setError(null); setStreamAssignments([]); }}
                                        className="px-8 py-5 text-slate-400 font-black text-[10px] uppercase tracking-[0.2em] hover:text-slate-600 transition-colors"
                                    >
                                        Dismiss
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving || splitting}
                                        className="flex-1 bg-[#18216D] text-white py-5 rounded-[1.5rem] font-black text-[11px] uppercase tracking-[0.2em] hover:bg-[#1e2985] shadow-2xl shadow-indigo-900/20 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                                    >
                                        {saving ? (
                                            <>
                                                <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                                <span>Synchronizing...</span>
                                            </>
                                        ) : (
                                            <><i className="fas fa-save"></i> <span>Update</span></>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Purge Confirmation */}
            {deletingArea && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md p-10 border border-white animate-in zoom-in-95 duration-200">
                        <div className="h-20 w-20 bg-rose-50 text-rose-500 rounded-3xl flex items-center justify-center text-3xl mb-8 animate-bounce mx-auto">
                            <i className="fas fa-skull-crossbones"></i>
                        </div>
                        <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight text-center">Irreversible Purge?</h3>
                        <p className="text-slate-500 mt-4 text-sm font-bold leading-relaxed text-center">
                            Are you certain you wish to purge <span className="text-[#18216D] underline">{deletingArea.name}</span>? This will truncate architectural linkages and cannot be undone.
                        </p>
                        <div className="mt-10 flex gap-4">
                            <button
                                onClick={() => setDeletingArea(null)}
                                className="flex-1 px-6 py-5 border-2 border-slate-100 text-slate-400 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-all"
                            >
                                Abort
                            </button>
                            <button
                                onClick={handleDelete}
                                className="flex-1 px-6 py-5 bg-rose-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-rose-700 shadow-xl shadow-rose-900/30 transition-all"
                            >
                                Confirm Purge
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Modal
                isOpen={showUnifyConfirm}
                onClose={() => setShowUnifyConfirm(false)}
                title="Unify Stream Instances"
                type="danger"
                footer={(
                    <>
                        <button
                            onClick={handleUnifyStreams}
                            className="bg-rose-600 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-lg shadow-rose-900/20 hover:bg-rose-700 transition-all"
                        >
                            Confirm Unification
                        </button>
                        <button
                            onClick={() => setShowUnifyConfirm(false)}
                            className="text-[#18216D] px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-slate-50 transition-all"
                        >
                            Cancel
                        </button>
                    </>
                )}
            >
                <div className="space-y-6">
                    <p className="text-slate-900 font-black uppercase text-xs tracking-widest border-b border-rose-100 pb-3">Warning: Irreversible Architectural Change</p>
                    <div className="space-y-4">
                        <div className="flex gap-4">
                            <div className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0 mt-1">
                                <i className="fas fa-trash-can text-[10px]"></i>
                            </div>
                            <p className="text-slate-500 text-sm font-bold leading-relaxed">
                                <span className="text-rose-600">DELETED:</span> All stream-specific Lesson Plans, Notes, Videos, and Modules will be permanently removed.
                            </p>
                        </div>
                        <div className="flex gap-4">
                            <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0 mt-1">
                                <i className="fas fa-shield-check text-[10px]"></i>
                            </div>
                            <p className="text-slate-500 text-sm font-bold leading-relaxed">
                                <span className="text-emerald-600">PRESERVED:</span> All Assignments, Quizzes, and Student Grades will be safely re-parented to the Master Area.
                            </p>
                        </div>
                        <div className="flex gap-4">
                            <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0 mt-1">
                                <i className="fas fa-rotate text-[10px]"></i>
                            </div>
                            <p className="text-slate-500 text-sm font-bold leading-relaxed">
                                <span className="text-indigo-600">REVERTED:</span> Curricular strands and SOW will default back to the Master Registry version.
                            </p>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CourseList;

